import React, { useMemo } from 'react';
import { Card, CardBody, CardHeader, Table, Badge, Button } from 'reactstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faNewspaper, 
  faImage, 
  faVideo, 
  faEye, 
  faHeart, 
  faComments, 
  faShareAlt,
  faMousePointer,
  faExternalLinkAlt
} from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

interface Post {
  id: string;
  message: string;
  type: 'image' | 'video' | 'text';
  created_time: string;
  reactions: number;
  comments: number;
  shares: number;
  clicks: number;
  impressions: number;
}

interface FacebookPostsTableProps {
  facebookInsights: any;
  selectedPeriod: string;
}

export const FacebookPostsTable: React.FC<FacebookPostsTableProps> = ({
  facebookInsights,
  selectedPeriod
}) => {
  // Simular datos de publicaciones (en una implementación real, esto vendría de la API)
  const posts: Post[] = useMemo(() => {
    // Esta es una simulación - en la implementación real se obtendría de la API de Facebook
    return [
      {
        id: '1',
        message: '👮💪 En Controll no solo observamos, actuamos....',
        type: 'image',
        created_time: '2025-10-03T11:06:00Z',
        reactions: 5,
        comments: 0,
        shares: 2,
        clicks: 6,
        impressions: 87
      },
      {
        id: '2',
        message: '🎉 ¡Felices Fiestas Patrias! 🇨🇱 ...',
        type: 'image',
        created_time: '2025-09-17T16:49:00Z',
        reactions: 1,
        comments: 0,
        shares: 0,
        clicks: 3,
        impressions: 45
      }
    ];
  }, [facebookInsights]);

  const getPostTypeIcon = (type: string) => {
    switch (type) {
      case 'image':
        return faImage;
      case 'video':
        return faVideo;
      default:
        return faNewspaper;
    }
  };

  const getPostTypeColor = (type: string) => {
    switch (type) {
      case 'image':
        return 'primary';
      case 'video':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const formatDate = (dateString: string) => {
    return moment(dateString).format('DD MMM YYYY HH:mm');
  };

  if (!posts || posts.length === 0) {
    return (
      <Card>
        <CardHeader>
          <h4>Publicaciones en el período</h4>
        </CardHeader>
        <CardBody>
          <div className="no-data">
            <FontAwesomeIcon icon={faNewspaper} size="2x" />
            <p>No hay publicaciones disponibles para este período</p>
          </div>
        </CardBody>
      </Card>
    );
  }

  return (
    <div className="facebook-posts-table">
      <Card>
        <CardHeader>
          <h4>Publicaciones en el período</h4>
        </CardHeader>
        <CardBody>
          <div className="table-responsive">
            <Table hover className="posts-table">
              <thead>
                <tr>
                  <th>Tipo</th>
                  <th>Fecha</th>
                  <th>Reacciones</th>
                  <th>Comentarios</th>
                  <th>Compartidos</th>
                  <th>Clics</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {posts.map((post) => (
                  <tr key={post.id}>
                    <td>
                      <div className="post-type">
                        <FontAwesomeIcon 
                          icon={getPostTypeIcon(post.type)} 
                          className="me-2"
                        />
                        <Badge color={getPostTypeColor(post.type)}>
                          {post.type === 'image' ? 'Imagen' : post.type === 'video' ? 'Video' : 'Texto'}
                        </Badge>
                      </div>
                    </td>
                    <td>
                      <div className="post-date">
                        {formatDate(post.created_time)}
                      </div>
                    </td>
                    <td>
                      <div className="metric-value">
                        <FontAwesomeIcon icon={faHeart} className="me-1" />
                        {post.reactions}
                      </div>
                    </td>
                    <td>
                      <div className="metric-value">
                        <FontAwesomeIcon icon={faComments} className="me-1" />
                        {post.comments}
                      </div>
                    </td>
                    <td>
                      <div className="metric-value">
                        <FontAwesomeIcon icon={faShareAlt} className="me-1" />
                        {post.shares}
                      </div>
                    </td>
                    <td>
                      <div className="metric-value">
                        <FontAwesomeIcon icon={faMousePointer} className="me-1" />
                        {post.clicks}
                      </div>
                    </td>
                    <td>
                      <div className="post-actions">
                        <Button 
                          size="sm" 
                          color="outline-primary" 
                          className="me-2"
                          title="Reutilizar texto"
                        >
                          <FontAwesomeIcon icon="calendar" />
                        </Button>
                        <Button 
                          size="sm" 
                          color="outline-secondary"
                          title="Ver publicación"
                        >
                          <FontAwesomeIcon icon={faExternalLinkAlt} />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </CardBody>
      </Card>
    </div>
  );
};
